import { useDashboardData } from '@/hooks/useDashboardData'
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { AchievementType } from '@/types'
import useFilterStore from '@/store/useFilterStore'

const ChampionshipMap = () => {
  const { data, isLoading, error, mutate } = useDashboardData()
  const { achievementType, setAchievementType } = useFilterStore()

  if (isLoading) {
    return (
      <div className="h-48 lg:h-64 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="h-48 lg:h-64 flex items-center justify-center text-red-600">
        データの読み込みに失敗しました
      </div>
    )
  }

  const { championshipLocations } = data.charts

     // データが空の場合は、タブと地図のみを表示
   const hasData = championshipLocations && championshipLocations.length > 0

  // デバッグ用ログ
  // console.log('Championship locations:', championshipLocations)

  // achievement_typeの表示名を取得
  const getAchievementTypeDisplayName = (type: AchievementType) => {
    switch (type) {
      case AchievementType.CHAMPIONSHIP:
        return '優勝'
      case AchievementType.FINALIST:
        return '優勝＋準優勝'
      case AchievementType.SEMIFINAL:
        return 'ベスト4以上'
      case AchievementType.QUARTERFINAL:
        return 'ベスト8以上'
      default:
        return '優勝'
    }
  }

  // 現在のachievement_typeを取得（最初のデータから）
  const currentAchievementType = championshipLocations[0]?.achievement_type || AchievementType.CHAMPIONSHIP
  const achievementDisplayName = getAchievementTypeDisplayName(currentAchievementType)

  const getMarkerColor = (championshipCount: number) => {
    if (championshipCount >= 10) return '#dc2626' // 赤（10回以上）
    if (championshipCount >= 5) return '#f97316'  // オレンジ（5-9回）- bg-orange-500と同じ色
    if (championshipCount >= 2) return '#eab308'  // 黄色（2-4回）- bg-yellow-500と同じ色
    if (championshipCount >= 1) return '#16a34a'  // 緑（1回）- bg-green-600と同じ色
    return '#6b7280' // グレー（0回）
  }

  const getMarkerRadius = (championshipCount: number) => {
    // 優勝回数に応じてマーカーのサイズを調整（0回の場合は最小サイズ）
    if (championshipCount === 0) return 6 // 0回の場合は小さめのマーカー
    return Math.max(8, Math.min(20, championshipCount * 2))
  }

  // タブ切り替えハンドラー
  const handleTabChange = (type: AchievementType) => {
    setAchievementType(type)
    mutate()
  }

     return (
     <div>
       {/* {hasData && (
         <div className="text-xs text-gray-500 mb-2">
           Debug: Championship locations count: {championshipLocations.length}
         </div>
       )} */}
      
      {/* タイトルとタブ */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          {<h2 className="text-base lg:text-lg font-semibold text-gray-800">
            大会成績ヒートマップ
          </h2>}
        </div>
        
        {/* タブ形式のachievement_type選択 */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {[
            AchievementType.CHAMPIONSHIP,
            AchievementType.FINALIST,
            AchievementType.SEMIFINAL,
            AchievementType.QUARTERFINAL
          ].map((type) => (
            <button
              key={type}
              onClick={() => handleTabChange(type)}
              className={`flex-1 px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium rounded-md transition-colors ${
                achievementType === type
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {getAchievementTypeDisplayName(type)}
            </button>
          ))}
        </div>
      </div>
      
       <div className="h-[640px] lg:h-[768px]">
         {/* マーカー説明（データがある場合のみ表示） */}
         {hasData && (
           <div className="mb-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
             <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-2 lg:space-y-0">
               <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                 <span className="text-sm font-medium text-gray-700">マーカー説明:</span>
                 
                 {/* 色の説明 */}
                 <div className="flex flex-wrap items-center gap-3">
                   <div className="flex items-center space-x-1">
                     <div className="w-3 h-3 rounded-full bg-red-600"></div>
                     <span className="text-xs text-gray-600">10回以上</span>
                   </div>
                   <div className="flex items-center space-x-1">
                     <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                     <span className="text-xs text-gray-600">5-9回</span>
                   </div>
                   <div className="flex items-center space-x-1">
                     <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                     <span className="text-xs text-gray-600">2-4回</span>
                   </div>
                   <div className="flex items-center space-x-1">
                     <div className="w-3 h-3 rounded-full bg-green-600"></div>
                     <span className="text-xs text-gray-600">1回</span>
                   </div>
                 </div>
               </div>
               
               {/* サイズの説明 */}
               <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
                 <span className="text-sm font-medium text-gray-700">マーカーサイズ:</span>
                 <div className="flex items-center space-x-2">
                   <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                   <span className="text-xs text-gray-600">小</span>
                   <div className="w-4 h-4 rounded-full bg-gray-400"></div>
                   <span className="text-xs text-gray-600">大</span>
                 </div>
               </div>
             </div>
           </div>
         )}
        
        <MapContainer
          center={[36, 138]}
          zoom={5}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
           {hasData && championshipLocations
             .filter(location => location.championship_count > 0) // 0回のマーカーは表示しない
             .map((location, index) => (
             <CircleMarker
               key={index}
               center={[location.latitude, location.longitude]}
               radius={getMarkerRadius(location.championship_count)}
               fillColor={getMarkerColor(location.championship_count)}
               color="white"
               weight={2}
               opacity={1}
               fillOpacity={0.7}
             >
               <Popup>
                 <div>
                   <strong>{location.team_name}</strong><br />
                   {location.prefecture}<br />
                   {achievementDisplayName}回数: {location.championship_count}回
                 </div>
               </Popup>
             </CircleMarker>
           ))}
        </MapContainer>
      </div>
    </div>
  )
}

export default ChampionshipMap
